import { Cake, CardGiftcard, CreditCard } from '@mui/icons-material';
import { Box, IconButton } from '@mui/material';
import { useState } from 'react';

import { default as blankMaleImgSrc } from '~/assets/img/blank-male.jpg';
import { useLoggedInEmployeeProfile } from '~/components/Auth';
import { SummaryItem } from '~/components/MyWorkspace/SummaryItem';
import { SanitizeHTML } from '~/components/SanitizeHTML';
import { TooltippedTruncatedText } from '~/components/TooltippedTruncatedText';
import { getRelativeTime } from '~/data/dateFormat';
import { LegacyFAIcon, LegacyFAIcons } from '~/legacy-components/LegacyFAIcon';
import { People } from '~/services/Http';

import { redirectByTopicRules } from '../../myNotificationsMapping';

import { useSxStyles } from './useStyles';

export interface Props {
  listItem: People.INotificationDto;
  className?: string;
  isOldUi?: boolean;
  hasBorder?: boolean;
  handleClose?: (id: string | number) => void;
  handleSecondaryAction?: (id: string | number) => void;
  customActions?: {
    onMarkAsRead: (id: number) => void;
    onMarkAsUnread: (id: number) => void;
    onDeleteNotification: (id: number) => void;
  };
}

const giftEmoji = '<emoji>gift</emoji>';
const birthdayCakeEmoji = '<emoji>birthday-cake</emoji>';
const creditCardEmoji = '<emoji>credit-card</emoji>';

export const Notification: React.FC<Props> = (props: Props) => {
  const { listItem, isOldUi, hasBorder, customActions } = props;

  const sxStyles = useSxStyles({ isRead: listItem.isRead });
  const loggedInEmployee = useLoggedInEmployeeProfile();
  const redirectRule = redirectByTopicRules.find(
    (rule) => rule[0] === listItem.topic.code,
  );
  const area = redirectRule ? redirectRule[1] : undefined;
  const subArea = redirectRule ? redirectRule[2] : undefined;
  const [isHovered, setIsHovered] = useState(false);

  const listItemId = listItem.id;

  return (
    <Box
      onMouseOver={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{ listStyleType: 'none' }}
    >
      <SummaryItem
        isOldUi={isOldUi}
        customSxStyles={{
          listItemRoot: {
            ...(isOldUi && sxStyles.notificationRoot),
            ...(!listItem.isRead && sxStyles.unreadNotificationRoot),
            ...(hasBorder && sxStyles.border),
          },
        }}
        avatarProps={{
          src:
            listItem.target && listItem.target.profilePic
              ? listItem.target.profilePic
              : blankMaleImgSrc,
        }}
        primaryElement={
          <Box sx={sxStyles.primaryElement}>
            <TooltippedTruncatedText>
              <SanitizeHTML
                sx={sxStyles.notificationContent}
                html={listItem.content
                  .replace(giftEmoji, '')
                  .replace(birthdayCakeEmoji, '')
                  .replace(creditCardEmoji, '')}
              />
            </TooltippedTruncatedText>
            {listItem.content.includes(giftEmoji) && (
              <CardGiftcard sx={sxStyles.redIcon} />
            )}
            {listItem.content.includes(birthdayCakeEmoji) && (
              <Cake sx={sxStyles.redIcon} />
            )}
            {listItem.content.includes(creditCardEmoji) && (
              <CreditCard sx={sxStyles.creditCardIcon} />
            )}
          </Box>
        }
        secondaryElement={
          <strong>{getRelativeTime(listItem.createdAt)}</strong>
        }
        secondaryActions={
          isHovered ? (
            <Box sx={sxStyles.actionBox}>
              {listItem.isRead && customActions?.onMarkAsUnread ? (
                <IconButton
                  onClick={() => customActions.onMarkAsUnread(listItemId)}
                  size={'small'}
                  sx={{ ...sxStyles.iconButton, ...sxStyles.iconSpacing }}
                >
                  <LegacyFAIcon
                    FAIcon={LegacyFAIcons.EyeSlash}
                    sx={sxStyles.icon}
                  />
                </IconButton>
              ) : null}
              {!!customActions?.onDeleteNotification && (
                <IconButton
                  onClick={() => customActions.onDeleteNotification(listItemId)}
                  size={'small'}
                  sx={sxStyles.iconButton}
                >
                  <LegacyFAIcon
                    FAIcon={LegacyFAIcons.Reject}
                    sx={sxStyles.icon}
                  />
                </IconButton>
              )}
            </Box>
          ) : (
            <></>
          )
        }
        onClick={() => {
          if (customActions?.onMarkAsRead) {
            customActions.onMarkAsRead(listItemId);
          }
        }}
        href={
          area
            ? `/page-notification-redirect?topic=${listItem.topic.code}&id=${
                listItem.id
              }&area=${area}&subarea=${subArea}&empId=${
                listItem.target ? listItem.target.id : loggedInEmployee.id
              }`
            : undefined
        }
      />
    </Box>
  );
};
